// Shared client helper for the post/chat image attachment flow. Mirrors the backend
// constraints in backend/src/routes/uploads.ts (5 MB, PNG/JPG/WEBP/GIF, magic-byte checked
// server-side) and maps the stable server error codes to friendly, user-facing messages.
import { apiUploadImage } from '../services/api';

export const MAX_IMAGE_BYTES = 5 * 1024 * 1024; // 5 MB — matches uploads.ts MAX_BYTES
export const ACCEPTED_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/gif'];
// `accept` attribute value for <input type="file"> pickers.
export const IMAGE_ACCEPT_ATTR = ACCEPTED_IMAGE_TYPES.join(',');

/** Map a stable upload error code (or thrown error) to a friendly message. */
export function imageUploadErrorMessage(err: unknown): string {
  const code = (err as any)?.info?.error || (err as any)?.message || '';
  switch (code) {
    case 'file_too_large':
      return 'Image must be under 5 MB.';
    case 'unsupported_file_type':
      return 'Use a PNG, JPG, WEBP, or GIF.';
    case 'no_file':
      return 'No image was selected.';
    default:
      return 'Upload failed. Please try again.';
  }
}

/**
 * Cheap client-side pre-check so obviously-invalid files fail instantly without a round
 * trip. Returns a friendly error message, or null if the file looks acceptable. The server
 * remains the source of truth (it sniffs magic bytes).
 */
export function validateImageFile(file: File): string | null {
  if (file.size > MAX_IMAGE_BYTES) return 'Image must be under 5 MB.';
  // Some browsers leave type empty for exotic files; let those through to the server check.
  if (file.type && !ACCEPTED_IMAGE_TYPES.includes(file.type)) {
    return 'Use a PNG, JPG, WEBP, or GIF.';
  }
  return null;
}

/**
 * Whether a stored image URL is safe to render (<img src>) or open in a new tab. Only
 * http(s) URLs are allowed — this blocks dangerous schemes like `javascript:` / `data:`
 * from reaching navigation sinks (window.open / anchor href) if a malicious value ever
 * gets persisted. Defense-in-depth alongside the server-side validator.
 */
export function isSafeHttpUrl(url?: string | null): boolean {
  if (!url) return false;
  try {
    const u = new URL(url, window.location.origin);
    return u.protocol === 'http:' || u.protocol === 'https:';
  } catch {
    return false;
  }
}

/**
 * Validate then upload an image, returning its absolute URL. Throws an Error whose message
 * is already user-friendly (so callers can surface `e.message` directly).
 */
export async function uploadImage(file: File): Promise<string> {
  const preError = validateImageFile(file);
  if (preError) throw new Error(preError);
  try {
    const { url } = await apiUploadImage(file);
    return url;
  } catch (err) {
    throw new Error(imageUploadErrorMessage(err));
  }
}
